/**
 * ValidationForm - Main form component with dynamic field grouping
 * Groups fields by uiGroup from analyzer configuration: Verification vs New Information
 * Fields are built dynamically from analyzer settings (no hardcoded values)
 */

import React, { useMemo, useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import { useValidation } from '../../contexts/ValidationContext';
import FieldGroup from './FieldGroup';
import { logFieldValidation } from '../../utils/fieldValidationUtils';
import analyzerService, { AnalyzerConfiguration, FieldConfiguration } from '../../services/analyzerService';
import { ExtractedField } from '../../types';

// Styled components
const FormContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
}));

const FormContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

export interface ValidationFormProps {
  className?: string;
}

const ValidationForm: React.FC<ValidationFormProps> = ({ className }) => {
  const { state, actions } = useValidation();
  const [analyzerConfig, setAnalyzerConfig] = useState<AnalyzerConfiguration | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  
  // Add field validation logging for debugging
  useEffect(() => {
    if (state.document?.fields) {
      logFieldValidation(state.document.fields);
    }
  }, [state.document?.fields]);

  // Load analyzer configuration based on document type
  useEffect(() => {
    async function loadAnalyzerConfig() {
      if (!state.document?.metadata?.documentType) {
        setLoadingConfig(false);
        return;
      }

      try {
        setLoadingConfig(true);
        const configs = await analyzerService.getAnalyzerConfigurations();
        
        // Find analyzer config matching document type
        const config = configs.find(c => c.documentType === state.document?.metadata?.documentType);
        
        if (config) {
          setAnalyzerConfig(config);
        } else {
          console.warn(`No analyzer configuration found for document type: ${state.document?.metadata?.documentType}`);
          setAnalyzerConfig(null);
        }
      } catch (error) {
        console.error('Failed to load analyzer configuration:', error);
        setAnalyzerConfig(null);
      } finally {
        setLoadingConfig(false);
      }
    }

    loadAnalyzerConfig();
  }, [state.document?.metadata?.documentType]);

  // Group fields dynamically based on analyzer configuration
  const fieldGroups = useMemo(() => {
    if (!state.document || !analyzerConfig) {
      return { verification: [], newInformation: [], readOnlyFields: [] };
    }

    const allFields = state.document.fields;
    
    // Build field configuration map for quick lookup. The Validate stage shows every enabled
    // field that isn't explicitly hidden — visibility is driven by `uiGroup`, not the old
    // fixed-stage `stageUsage` triple (which no longer gates HITL rendering).
    const fieldConfigMap = new Map<string, FieldConfiguration>();
    analyzerConfig.fields.forEach(fieldConfig => {
      if (fieldConfig.enabled && fieldConfig.uiGroup !== 'hidden') {
        fieldConfigMap.set(fieldConfig.fieldName, fieldConfig);
      }
    });

    // Filter and group fields based on analyzer configuration
    const verificationFields: any[] = [];
    const newInformationFields: any[] = [];
    const readOnlyFieldNames: string[] = [];

    allFields.forEach(field => {
      const fieldConfig = fieldConfigMap.get(field.name);
      
      // Skip fields not in analyzer config or not enabled for dataValidation stage
      if (!fieldConfig) return;
      
      // Skip hidden fields
      if (fieldConfig.uiGroup === 'hidden') return;

      // Track read-only fields
      if (fieldConfig.readOnly) {
        readOnlyFieldNames.push(field.name);
      }

      // Enhance field with configuration metadata + field schema (Sprint M2).
      // Map the schema dataType onto fieldType so the Data Type setting actually drives
      // HITL input rendering (e.g. DatePicker) and validator routing.
      const enhancedField = {
        ...field,
        displayName: fieldConfig.displayName || field.name,
        required: fieldConfig.required,
        readOnly: fieldConfig.readOnly,
        displayOrder: fieldConfig.displayOrder,
        fieldType: dataTypeToFieldType(fieldConfig.dataType) ?? field.fieldType,
        format: fieldConfig.format,
        confidenceThreshold: fieldConfig.confidenceThreshold,
        validationRule: fieldConfig.validationRule
      };

      // Group by uiGroup
      if (fieldConfig.uiGroup === 'verification') {
        verificationFields.push(enhancedField);
      } else if (fieldConfig.uiGroup === 'newInformation') {
        newInformationFields.push(enhancedField);
      }
    });

    // Sort fields by displayOrder
    verificationFields.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    newInformationFields.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

    return {
      verification: verificationFields,
      newInformation: newInformationFields,
      readOnlyFields: readOnlyFieldNames,
    };
  }, [state.document, analyzerConfig]);

  // Handle field value changes
  const handleFieldChange = useCallback((fieldId: string, value: string) => {
    actions.updateField(fieldId, value);
  }, [actions]);

  if (!state.document) {
    return (
      <FormContainer className={className}>
        <Typography variant="h6" color="text.secondary" align="center">
          No document loaded
        </Typography>
      </FormContainer>
    );
  }

  if (loadingConfig) {
    return (
      <FormContainer className={className}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      </FormContainer>
    );
  }

  if (!analyzerConfig) {
    return (
      <FormContainer className={className}>
        <Typography variant="h6" color="error" align="center">
          No analyzer configuration found for document type: {state.document.metadata?.documentType}
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
          Please configure an analyzer in Settings → Document Models
        </Typography>
      </FormContainer>
    );
  }

  return (
    <FormContainer className={className}>
      {/* Form Header */}
      <Box mb={3}>
        <Typography variant="h5" component="h2" gutterBottom>
          Document Validation
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Review verification fields for accuracy, then complete new information fields for system submission.
        </Typography>
        <Divider sx={{ mt: 2 }} />
      </Box>

      {/* Scrollable Form Content */}
      <FormContent>
        {/* Verification Fields Group */}
        {fieldGroups.verification.length > 0 && (
          <FieldGroup
            title="Verification"
            description="Cross-reference these extracted values with the document"
            fields={fieldGroups.verification}
            groupType="verification"
            readOnlyFields={fieldGroups.readOnlyFields}
            validatedFields={state.validatedFields}
            onFieldChange={handleFieldChange}
            disabled={state.isSubmitting}
          />
        )}

        {/* New Information Fields Group */}
        {fieldGroups.newInformation.length > 0 && (
          <Box mb={3}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              New Information
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Complete and verify these fields for system submission
            </Typography>
            
            <FieldGroup
              title=""
              description=""
              fields={fieldGroups.newInformation}
              groupType="newInformation"
              validatedFields={state.validatedFields}
              onFieldChange={handleFieldChange}
              disabled={state.isSubmitting}
            />
          </Box>
        )}

        {fieldGroups.verification.length === 0 && fieldGroups.newInformation.length === 0 && (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              No fields configured for validation
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Configure fields in Settings → Document Models
            </Typography>
          </Box>
        )}
      </FormContent>
    </FormContainer>
  );
};

/**
 * Map an analyzer schema dataType onto the HITL field's display/validation type.
 * string/address/boolean render as text inputs; number/currency/date get their typed handling.
 * Returns undefined when no dataType is set, so the caller keeps the extraction-time fieldType.
 */
function dataTypeToFieldType(
  dataType: FieldConfiguration['dataType']
): ExtractedField['fieldType'] | undefined {
  switch (dataType) {
    case 'date': return 'date';
    case 'number': return 'number';
    case 'currency': return 'currency';
    case 'string':
    case 'address':
    case 'boolean': return 'text';
    default: return undefined;
  }
}

export default ValidationForm;